import { serieTieneAcciones } from '../utils/series-with-shares.util';
import { Injectable, signal, inject, computed } from '@angular/core';
import { FormBuilder, FormGroup, AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ClientsService } from '../../../../core/services/clients/clients.service';
import { SerieData } from '../models/serie-data';
import { formatShareAmountEs } from '../utils/format-share-amount';

export interface OperationCapitalSocialPayload {
  hasSeries?: boolean;
  totalShares?: number | null;
  nominalValue?: number | null;
  sharesNumberFrom?: number | null;
  sharesNumberTo?: number | null;
  series?: Array<{
    id?: number | null;
    name?: string;
    totalShares?: number | null;
    nominalValue?: number | null;
    sharesNumberFrom?: number | null;
    sharesNumberTo?: number | null;
  }>;
}

@Injectable()
export class ConstitutionCapitalStore {
  private readonly clientsService = inject(ClientsService);
  private readonly fb = inject(FormBuilder);

  readonly capitalSocialForm: FormGroup = this.fb.nonNullable.group({
    hasSeries: this.fb.nonNullable.control<boolean>(false),
    totalShares: this.fb.control<number | null>(null),
    nominalValue: this.fb.control<number | null>(null),
    sharesNumberFrom: this.fb.control<number | null>(null),
    sharesNumberTo: this.fb.control<number | null>(null)
  }, { validators: [this.rangeValidator('sharesNumberFrom', 'sharesNumberTo'), this.totalSharesMatchNumerationValidator()] });

  readonly modalForm: FormGroup = this.fb.nonNullable.group({
    id: this.fb.control<number | null>(null),
    name: this.fb.nonNullable.control<string>('', { validators: [Validators.required] }),
    totalShares: this.fb.control<number | null>(null),
    nominalValue: this.fb.control<number | null>(null),
    sharesNumberFrom: this.fb.control<number | null>(null),
    sharesNumberTo: this.fb.control<number | null>(null)
  }, { validators: [this.rangeValidator('sharesNumberFrom', 'sharesNumberTo'), this.totalSharesMatchNumerationValidator()] });

  readonly hasSeries = signal<boolean>(false);
  readonly series = signal<SerieData[]>([]);
  readonly nextSerieId = signal<number>(1);
  readonly seriesTableConfig = signal<any | null>(null);
  readonly isModalActive = signal<boolean>(false);
  readonly isCreating = signal<boolean>(true);
  readonly singleSeriesTotal = signal<number>(0);
  editIndex: number | null = null;

  /** Optional hook for wizard: re-sync socios when series change in deferPersistence mode */
  private onSeriesChangedCallback: (() => void) | null = null;

  /** DB ids loaded from API — used to distinguish real ids from local temp ids in the wizard */
  private readonly loadedDbSeriesIds = signal<Set<number>>(new Set());

  readonly totalCompanyShares = computed(() => {
    if (this.hasSeries()) {
      return this.series().reduce((sum, s) => sum + (s.totalShares || 0), 0);
    }
    return this.singleSeriesTotal();
  });

  readonly seriesOptions = computed(() =>
    this.series().filter(serieTieneAcciones).map(s => ({ label: s.name, value: s.id }))
  );

  readonly seriesTotalsMap = computed(() => {
    const map = new Map<number, number>();
    this.series().forEach(s => {
      if (s.id !== null && s.totalShares !== null) {
        map.set(s.id, s.totalShares);
      }
    });
    return map;
  });

  setOnSeriesChanged(callback: (() => void) | null): void {
    this.onSeriesChangedCallback = callback;
  }

  private notifySeriesChanged(): void {
    this.onSeriesChangedCallback?.();
  }

  init(): void {
    this.capitalSocialForm.get('hasSeries')?.valueChanges.subscribe(value => {
      this.hasSeries.set(!!value);
      this.buildSeriesTableConfig();
    });
    this.capitalSocialForm.get('totalShares')?.valueChanges.subscribe(v =>
      this.singleSeriesTotal.set(Number(v) || 0)
    );
    this.buildSeriesTableConfig();
  }

  isDbSeriesId(id: number | null | undefined): boolean {
    if (id == null || id <= 0) return false;
    return this.loadedDbSeriesIds().has(id);
  }

  async loadCapitalData(clientId: number): Promise<void> {
    try {
      const capitalRes = await this.clientsService.getClientSocialCapital(clientId);
      const capitalData = Array.isArray(capitalRes) ? capitalRes[0] : capitalRes;

      const dbIds = new Set<number>();

      if (capitalData) {
        const singleCapital = capitalData.socialCapital ?? capitalData;

        this.capitalSocialForm.patchValue({
          hasSeries: capitalData.hasSeries,
          totalShares: singleCapital.totalShares ?? null,
          nominalValue: singleCapital.nominalValue ?? null,
          sharesNumberFrom: singleCapital.sharesNumberFrom ?? null,
          sharesNumberTo: singleCapital.sharesNumberTo ?? null
        });

        this.singleSeriesTotal.set(Number(singleCapital.totalShares) || 0);

        if (capitalData.series && Array.isArray(capitalData.series) && capitalData.series.length > 0) {
          const loadedSeries: SerieData[] = [];
          for (const s of capitalData.series.filter((row: any) => row.name)) {
            const parsedId = Number(s.id);
            const hasDbId = Number.isFinite(parsedId) && parsedId > 0;
            if (!hasDbId) {
              console.warn('[ConstitutionCapitalStore] Serie from API missing DB id:', s.name);
            }
            const dbId = hasDbId ? parsedId : this.nextSerieId();
            if (hasDbId) {
              dbIds.add(parsedId);
            }
            loadedSeries.push({
              id: dbId,
              feLocalId: dbId,
              name: s.name,
              totalShares: s.totalShares,
              nominalValue: s.nominalValue,
              sharesNumberFrom: s.sharesNumberFrom,
              sharesNumberTo: s.sharesNumberTo
            });
          }
          this.series.set(loadedSeries);
          this.buildSeriesTableConfig();
          const maxId = loadedSeries.reduce((max, s) => Math.max(max, s.id || 0), 0);
          this.nextSerieId.set(maxId + 1);
        } else {
          this.series.set([]);
        }
      }

      this.loadedDbSeriesIds.set(dbIds);
      this.hasSeries.set(!!capitalData.hasSeries);
    } catch (error) {
      console.error('Error loading Capital Social data:', error);
    }
  }

  /** Incluye series referenciadas por socios que el listado de capital no devolvió. */
  mergeReferencedSeries(rows: Array<{
    id: number;
    name: string;
    totalShares: number | null;
    nominalValue: number | null;
    sharesNumberFrom: number | null;
    sharesNumberTo: number | null;
  }>): void {
    if (!this.hasSeries() || rows.length === 0) return;
    const known = new Set(this.series().map(s => s.id));
    const extras: SerieData[] = [];
    for (const row of rows) {
      if (!Number.isFinite(row.id) || known.has(row.id)) continue;
      known.add(row.id);
      extras.push({ ...row, feLocalId: row.id });
    }
    if (extras.length === 0) return;
    this.series.update(list => [...list, ...extras]);
    this.loadedDbSeriesIds.update(ids => {
      const next = new Set(ids);
      for (const serie of extras) next.add(serie.id);
      return next;
    });
    this.buildSeriesTableConfig();
  }

  /**
   * Overlay operation snapshot onto the store, reconciling series ids with DB.
   */
  hydrateFromOperation(capitalSocial: OperationCapitalSocialPayload | null | undefined): void {
    if (!capitalSocial) return;

    const dbSeriesById = new Map(
      this.series().filter(s => this.isDbSeriesId(s.id)).map(s => [s.id, s])
    );
    const dbSeriesByName = new Map(
      this.series()
        .filter(s => this.isDbSeriesId(s.id) && s.name)
        .map(s => [s.name.trim().toLowerCase(), s])
    );

    this.capitalSocialForm.patchValue({
      hasSeries: capitalSocial.hasSeries ?? false,
      totalShares: capitalSocial.totalShares ?? null,
      nominalValue: capitalSocial.nominalValue ?? null,
      sharesNumberFrom: capitalSocial.sharesNumberFrom ?? null,
      sharesNumberTo: capitalSocial.sharesNumberTo ?? null,
    });

    if (capitalSocial.hasSeries && Array.isArray(capitalSocial.series) && capitalSocial.series.length > 0) {
      const reconciled: SerieData[] = capitalSocial.series.map(opSerie => {
        let dbId: number | undefined;
        if (opSerie.id != null && dbSeriesById.has(opSerie.id)) {
          dbId = opSerie.id;
        } else if (opSerie.name) {
          const byName = dbSeriesByName.get(opSerie.name.trim().toLowerCase());
          if (byName) dbId = byName.id;
        }
        const localId = dbId ?? this.nextSerieId();
        if (!dbId) {
          this.nextSerieId.update(n => n + 1);
        }
        const opFeLocalId = (opSerie as { feLocalId?: number }).feLocalId;
        const feLocalId = opFeLocalId != null ? Number(opFeLocalId) : localId;
        return {
          id: localId,
          feLocalId,
          name: opSerie.name ?? '',
          totalShares: opSerie.totalShares ?? null,
          nominalValue: opSerie.nominalValue ?? null,
          sharesNumberFrom: opSerie.sharesNumberFrom ?? null,
          sharesNumberTo: opSerie.sharesNumberTo ?? null,
        };
      });
      this.series.set(reconciled);
      this.buildSeriesTableConfig();
    }
  }

  /** Serialize store series for operationData, keeping local and DB ids. */
  serializeSeriesForOperation(): Array<Record<string, unknown>> {
    return this.series().map(s => {
      const payload: Record<string, unknown> = {
        name: s.name,
        totalShares: s.totalShares,
        nominalValue: s.nominalValue,
        sharesNumberFrom: s.sharesNumberFrom,
        sharesNumberTo: s.sharesNumberTo,
        feLocalId: s.feLocalId ?? s.id,
      };
      if (s.id != null) {
        payload['id'] = s.id;
      }
      return payload;
    });
  }

  buildCapitalSocialPayload(): Record<string, unknown> {
    const capital = this.capitalSocialForm.getRawValue();
    return {
      hasSeries: this.hasSeries(),
      totalShares: capital.totalShares,
      nominalValue: capital.nominalValue,
      sharesNumberFrom: capital.sharesNumberFrom,
      sharesNumberTo: capital.sharesNumberTo,
      series: this.serializeSeriesForOperation(),
    };
  }

  addSerie(): void {
    const id = this.nextSerieId();
    const newSerie: SerieData = {
      id,
      feLocalId: id,
      name: '',
      totalShares: null,
      nominalValue: null,
      sharesNumberFrom: null,
      sharesNumberTo: null
    };
    this.series.update(series => [...series, newSerie]);
    this.nextSerieId.update(id => id + 1);
    this.buildSeriesTableConfig();
    this.notifySeriesChanged();
  }

  removeSerie(id: number): void {
    this.series.update(series => series.filter(s => s.id !== id));
    this.buildSeriesTableConfig();
    this.notifySeriesChanged();
  }

  updateSerie(id: number, field: keyof Omit<SerieData, 'id'>, value: any): void {
    this.series.update(series => series.map(s => s.id === id ? { ...s, [field]: value } : s));
    this.buildSeriesTableConfig();
  }

  buildSeriesTableConfig(): void {
    const headers = [
      { name: 'Tipo/Serie', key: 'name' },
      { name: 'Nº acciones total', key: 'totalSharesDisplay', textAlign: 'right' },
      { name: 'Valor nominal', key: 'nominalValueDisplay', textAlign: 'right' },
      { name: 'Desde', key: 'sharesNumberFromDisplay', textAlign: 'right' },
      { name: 'Hasta', key: 'sharesNumberToDisplay', textAlign: 'right' },
      { name: 'Capital social', key: 'capitalSocial', textAlign: 'right' },
      { name: 'Acciones', key: 'delete' }
    ];
    const data = this.series().map(s => ({
      ...s,
      totalSharesDisplay: formatShareAmountEs(s.totalShares ?? 0),
      nominalValueDisplay: this.formatEs(s.nominalValue),
      sharesNumberFromDisplay:
        s.sharesNumberFrom != null ? formatShareAmountEs(s.sharesNumberFrom) : '',
      sharesNumberToDisplay:
        s.sharesNumberTo != null ? formatShareAmountEs(s.sharesNumberTo) : '',
      capitalSocial: `${this.formatEs(this.calcSerieTotal(s))} €`
    }));
    this.seriesTableConfig.set({ headers, data });
  }

  openFormModal(index?: number): void {
    if (index === undefined) {
      this.isCreating.set(true);
      this.editIndex = null;
      this.modalForm.reset({
        id: null,
        name: '',
        totalShares: null,
        nominalValue: null,
        sharesNumberFrom: null,
        sharesNumberTo: null
      });
    } else {
      this.isCreating.set(false);
      this.editIndex = index;
      const serie = this.series()[index];
      this.modalForm.patchValue(serie);
    }
    this.isModalActive.set(true);
  }

  closeModal(): void {
    this.isModalActive.set(false);
  }

  saveItem(clientId: number | null, deferPersistence = false): boolean {
    if (this.modalForm.invalid) return false;
    const v = this.modalForm.getRawValue();
    let structuralChange = false;
    if (this.isCreating()) {
      const id = this.nextSerieId();
      const newSerie: SerieData = {
        id,
        feLocalId: id,
        name: v.name,
        totalShares: v.totalShares,
        nominalValue: v.nominalValue,
        sharesNumberFrom: v.sharesNumberFrom,
        sharesNumberTo: v.sharesNumberTo
      };
      this.series.update(list => [...list, newSerie]);
      this.nextSerieId.update(id => id + 1);
      structuralChange = true;
      // Una serie nueva sobre un capital sin series lo convierte en capital con series; si no, el back descarta las series.
      if (!this.hasSeries()) this.capitalSocialForm.patchValue({ hasSeries: true });
    } else if (this.editIndex !== null) {
      const prev = this.series()[this.editIndex];
      const prevName = prev?.name?.trim().toLowerCase() ?? '';
      const nextName = (v.name ?? '').trim().toLowerCase();
      structuralChange = prevName !== nextName;
      this.series.update(list =>
        list.map((s, i) =>
          i === this.editIndex
            ? { ...s, ...v, id: s.id, feLocalId: s.feLocalId ?? s.id }
            : s,
        ),
      );
    }
    this.buildSeriesTableConfig();
    this.isModalActive.set(false);
    if (structuralChange) {
      this.notifySeriesChanged();
    }
    if (clientId !== null && !deferPersistence) {
      const payload = this.isCreating()
        ? v
        : { ...v, id: this.series()[this.editIndex ?? 0]?.id };
      this.clientsService.saveClientSocialCapital(clientId, this.hasSeries(), payload);
    }
    return structuralChange;
  }

  async deleteItem(index: number, deferPersistence = false): Promise<void> {
    const serie = this.series()[index];
    this.series.update(list => list.filter((_, i) => i !== index));
    this.buildSeriesTableConfig();
    this.notifySeriesChanged();

    if (!deferPersistence && serie?.id && this.isDbSeriesId(serie.id)) {
      try {
        await this.clientsService.deleteClientSocialCapital(serie.id);
      } catch (error) {
        console.error('Error deleting social capital serie:', error);
      }
    }
  }

  calcSerieTotal(serie: SerieData): number {
    const numAcciones = serie.totalShares || 0;
    const valorNominal = serie.nominalValue || 0;
    return numAcciones * valorNominal;
  }

  calcTotalSeries(): number {
    return this.series().reduce((acc, s) => acc + this.calcSerieTotal(s), 0);
  }

  formatEs(value: number | null | undefined): string {
    const v = Number(value || 0);
    return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true });
  }

  formatShareCount(value: number | null | undefined): string {
    return formatShareAmountEs(value);
  }

  private rangeValidator(desdeKey: string, hastaKey: string): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const desdeCtrl = group.get(desdeKey);
      const hastaCtrl = group.get(hastaKey);
      if (!desdeCtrl || !hastaCtrl) return null;
      const desde = Number(desdeCtrl.value ?? NaN);
      const hasta = Number(hastaCtrl.value ?? NaN);
      if (isNaN(desde) || isNaN(hasta)) return null;
      return hasta >= desde ? null : { rangeInvalid: true };
    };
  }

  private totalSharesMatchNumerationValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const totalCtrl = group.get('totalShares');
      const desdeCtrl = group.get('sharesNumberFrom');
      const hastaCtrl = group.get('sharesNumberTo');
      if (!totalCtrl || !desdeCtrl || !hastaCtrl) return null;

      const total = Number(totalCtrl.value ?? NaN);
      const desde = Number(desdeCtrl.value ?? NaN);
      const hasta = Number(hastaCtrl.value ?? NaN);

      if (isNaN(total) || isNaN(desde) || isNaN(hasta)) return null;
      if (hasta < desde) return null;

      const rangeCount = hasta - desde + 1;
      if (total === rangeCount) return null;

      return {
        sharesCountMismatch: {
          actualCount: total,
          expectedCount: rangeCount
        }
      };
    };
  }
}
